import { ScheduleApplicationStatus } from "../types/domains/accounting/schedule";
import { TransactionType } from "../types/transaction";
import { isScheduleLockingAccrualLedger } from "./schedule-transaction-field-lock";

export interface ManualTransactionUpdateEligibilityInput {
    type: TransactionType;
    reconciledInId?: string | null;
    splitParentId?: number | null;
    pairedTransferId?: number | null;
    schedule?: { status: ScheduleApplicationStatus } | null;
    isMatchedToInvoiceOrBill: boolean;
}

export const MANUAL_TRANSACTION_UPDATE_BLOCKERS = {
    notManual: "Only manually created transactions can be updated",
    reconciled: "Reconciled transactions can't be updated",
    split: "Unsplit the transaction to update it",
    transferMatched: "Unmatch the transfer to update this transaction",
    invoiceOrBillMatched:
        "Unmatch the invoice or bill to update this transaction",
    schedule: "Remove the recognition schedule to update this transaction",
} as const;

const MANUALLY_CREATED_TRANSACTION_TYPES: TransactionType[] = [
    TransactionType.MANUAL,
    TransactionType.CHECK,
    TransactionType.DEPOSIT,
];

export function isManuallyCreatedTransactionType(
    type: TransactionType,
): boolean {
    return MANUALLY_CREATED_TRANSACTION_TYPES.includes(type);
}

export const MANUAL_TRANSACTION_LOCKED_MESSAGE =
    "Locked transactions can't be updated";

export const MANUAL_TRANSACTION_MOVE_INTO_LOCK_MESSAGE =
    "Can't move a transaction into a locked period";

/**
 * Returns why a transaction's date, amount and bank description can't be
 * updated, or `null` when they can. Lock dates are checked separately because
 * the frontend and backend read them from different sources.
 */
export function getManualTransactionUpdateBlocker(
    transaction: ManualTransactionUpdateEligibilityInput,
): string | null {
    if (!isManuallyCreatedTransactionType(transaction.type)) {
        return MANUAL_TRANSACTION_UPDATE_BLOCKERS.notManual;
    }
    if (transaction.reconciledInId) {
        return MANUAL_TRANSACTION_UPDATE_BLOCKERS.reconciled;
    }
    if (transaction.splitParentId != null) {
        return MANUAL_TRANSACTION_UPDATE_BLOCKERS.split;
    }
    if (transaction.pairedTransferId != null) {
        return MANUAL_TRANSACTION_UPDATE_BLOCKERS.transferMatched;
    }
    if (transaction.isMatchedToInvoiceOrBill) {
        return MANUAL_TRANSACTION_UPDATE_BLOCKERS.invoiceOrBillMatched;
    }
    if (
        transaction.schedule &&
        isScheduleLockingAccrualLedger(transaction.schedule.status)
    ) {
        return MANUAL_TRANSACTION_UPDATE_BLOCKERS.schedule;
    }
    return null;
}
